// xhrJson — JSON-over-XHR helper that bypasses window.fetch entirely.
//
// Why this exists: in some user environments (Capacitor native HTTP shim,
// certain browser extensions, stale service workers) the global `fetch`
// response body gets consumed before our application code can read it,
// producing the dreaded "Failed to execute 'text'/'json' on 'Response':
// body stream already read" error.
//
// XHR responses do NOT have a streamable body — `responseText` is a plain
// string that can be read any number of times — so this fully sidesteps the
// problem for our write endpoints (POST/PUT/DELETE).
//
// Also attaches the impersonation token (when set in sessionStorage) so admin
// "View as user" sessions ride through every write request transparently.
//
// Usage:
//   const { ok, status, data } = await xhrJson("POST", url, { name: "X" });
//   if (!ok) throw new Error(data.detail || "Save failed");

export function getImpersonationToken() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage.getItem("imp_token") : null;
  } catch {
    return null;
  }
}

export function xhrJson(method, url, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    if (body !== undefined && body !== null) {
      xhr.setRequestHeader("Content-Type", "application/json");
    }
    const impTok = getImpersonationToken();
    if (impTok) xhr.setRequestHeader("X-Impersonation-Token", impTok);
    xhr.withCredentials = true;
    xhr.onload = () => {
      let data;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        data = { detail: xhr.responseText || "Server error" };
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(body !== undefined && body !== null ? JSON.stringify(body) : null);
  });
}
